import React, {
  ForwardedRef,
  ReactEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import ScrollContext from 'containers/ScrollContext';
import AmpContext from 'utils/AmpContext';
import { getWebpSrc } from 'utils/filestack';

type BaseImageProps = {
  className?: string;
  height?: number;
  width?: number;
  onLoad?: ReactEventHandler<HTMLImageElement>;
  src: string;
  srcSet?: string | null | undefined;
  alt?: string;
  'data-qa-id'?: string;
  svg?: boolean;
  webp?: boolean;
  quality?: number;
};

const BaseImg = forwardRef(
  (
    {
      className,
      src,
      srcSet,
      svg,
      webp,
      quality,
      height,
      width,
      onLoad,
      alt,
      'data-qa-id': dataQaId,
    }: BaseImageProps,
    ref: ForwardedRef<HTMLImageElement>
  ) => {
    const webpSrc = getWebpSrc(srcSet || src, {
      alwaysConvert: webp,
      quality,
    });

    return (
      <picture>
        {!svg && <source srcSet={webpSrc} type="image/webp" />}
        <source srcSet={srcSet || src} />
        <img
          className={className}
          src={src}
          alt={alt || ''}
          ref={ref}
          height={height}
          width={width}
          onLoad={onLoad}
          data-qa-id={dataQaId}
        />
      </picture>
    );
  }
);

type ImgProps = BaseImageProps & {
  alwaysShow?: boolean;
  className?: string;
  src: string;
  layout?: string;
  onLoad?: () => void;
  quality?: number;
};

export const Image = forwardRef(
  (
    {
      className,
      layout,
      src,
      srcSet,
      alwaysShow,
      onLoad,
      quality,
      svg,
      height,
      width,
      alt,
      webp,
      'data-qa-id': dataQaId,
    }: ImgProps,
    ref: ForwardedRef<HTMLImageElement>
  ) => {
    const [show, setShow] = useState(!!alwaysShow);
    const [, scrollY] = useContext(ScrollContext);
    const lazyRef = useRef(null);
    const update = useCallback(() => {
      if (!show && lazyRef && lazyRef.current) {
        const offsetY = lazyRef.current.getBoundingClientRect().top - 200;
        const inView = offsetY < window.innerHeight + scrollY;

        if (inView && !show) {
          setShow(true);
        }
      }
    }, [scrollY, show]);

    useEffect(() => {
      update();
    }, [scrollY, update]);

    return (
      <AmpContext.Consumer>
        {(requestIsAmp) => {
          if (requestIsAmp) {
            return (
              <amp-img
                layout={layout}
                src={src}
                srcSet={srcSet}
                class={className}
                height={height}
                width={width}
                alt={alt}
                webp={webp}
                data-qa-id={dataQaId}
              />
            );
          }

          if (!show) return <div ref={lazyRef} className={className} />;

          return (
            <BaseImg
              className={className}
              ref={ref}
              src={src}
              srcSet={srcSet}
              svg={svg}
              onLoad={onLoad}
              quality={quality}
              height={height}
              width={width}
              alt={alt}
              webp={webp}
              data-qa-id={dataQaId}
            />
          );
        }}
      </AmpContext.Consumer>
    );
  }
);
